import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  StarIcon,
  GiftIcon,
  ChartBarIcon,
  UserGroupIcon,
  ReceiptPercentIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DashboardBILT = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merchantStats, setMerchantStats] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Dashboard data fetch timeout, rendering with available data');
        setLoading(false);
      }
    }, 10000);

    fetchDashboardData();

    return () => clearTimeout(timeout);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/dashboard`);
      setDashboardData(response.data);

      // Fetch merchant stats if merchant
      if (response.data?.user?.role === 'merchant' || user?.role === 'merchant') {
        try {
          const statsRes = await axios.get(`${API_URL}/merchants/stats`);
          setMerchantStats(statsRes.data);
        } catch (e) {
          console.error('Error fetching merchant stats:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({ user: user, recentRentPayments: [], recentTransactions: [] });

      // Still try merchant stats
      if (user?.role === 'merchant') {
        try {
          const statsRes = await axios.get(`${API_URL}/merchants/stats`);
          setMerchantStats(statsRes.data);
        } catch (e) {
          console.error('Error fetching merchant stats:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const userData = dashboardData?.user || user;
  const points = Math.round(userData?.currentRewardBalance || 0);
  const isMerchant = userData?.role === 'merchant';

  // Get current date
  const getCurrentDate = () => {
    const date = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  // Merchant Dashboard View
  if (isMerchant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent mb-2">
              Merchant Dashboard
            </h1>
            <p className="text-gray-600 text-lg">{getCurrentDate()}</p>
          </div>

          {/* Add Restaurant Card - Enhanced */}
          <div className="relative bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 rounded-2xl shadow-2xl p-8 mb-8 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-4">
                  <StarIcon className="h-8 w-8 mr-3" />
                  <h2 className="text-3xl font-bold">Grow Your Business</h2>
                </div>
                <p className="text-lg text-white/90 mb-6 max-w-2xl">
                  Join LOCU and reach thousands of renters in your neighborhood. Get discovered, earn more, and build lasting customer relationships.
                </p>
                <div className="flex gap-4">
                  <Link
                    to="/merchant/dashboard"
                    className="inline-flex items-center px-6 py-3 bg-white text-primary-600 rounded-xl hover:bg-gray-100 font-semibold transition-all transform hover:scale-105 shadow-lg"
                  >
                    View Dashboard
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </Link>
                  <Link
                    to="/merchant/add-business"
                    className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl hover:bg-white/30 font-semibold transition-all transform hover:scale-105"
                  >
                    Add Business
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block ml-8">
                <div className="w-48 h-48 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <svg className="w-32 h-32 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats - Enhanced */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-6 border border-blue-200 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Restaurants</h3>
                <ChartBarIcon className="h-6 w-6 text-blue-500" />
              </div>
              <p className="text-4xl font-bold text-blue-900 mb-1">{merchantStats?.totalBusinesses ?? '-'}</p>
              <p className="text-xs text-blue-600">{merchantStats?.totalBusinesses ? `${merchantStats.totalTransactions} transactions` : 'Add your first restaurant'}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl shadow-lg p-6 border border-green-200 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide">Revenue</h3>
                <CurrencyDollarIcon className="h-6 w-6 text-green-500" />
              </div>
              <p className="text-4xl font-bold text-green-900 mb-1">₹{(merchantStats?.totalRevenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-green-600">From LOCU transactions</p>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl shadow-lg p-6 border border-primary-200 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-primary-700 uppercase tracking-wide">Commission</h3>
                <GiftIcon className="h-6 w-6 text-primary-500" />
              </div>
              <p className="text-4xl font-bold text-primary-900 mb-1">₹{(merchantStats?.monthlyCommission || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-primary-600">{merchantStats?.pendingCount ? `${merchantStats.pendingCount} pending` : 'This month\'s LOCU fee'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Hola, {userData?.name?.split(' ')[0] || 'there'}! 👋
          </h1>
          <p className="text-gray-600 text-lg">{getCurrentDate()}</p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/pay-rent"
            className="group relative bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <CurrencyDollarIcon className="h-12 w-12 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Pay Rent</h3>
              <p className="text-white/90 text-sm mb-4">Earn 2% points on every payment</p>
              <div className="flex items-center text-sm font-semibold">
                Get Started
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          <Link
            to="/neighborhood"
            className="group relative bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <StarIcon className="h-12 w-12 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Neighborhood</h3>
              <p className="text-white/90 text-sm mb-4">Discover restaurants nearby</p>
              <div className="flex items-center text-sm font-semibold">
                Explore
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          <Link
            to="/dining-events"
            className="group relative bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl shadow-xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative">
              <UserGroupIcon className="h-12 w-12 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Connect</h3>
              <p className="text-white/90 text-sm mb-4">Meet neighbors over great food</p>
              <div className="flex items-center text-sm font-semibold">
                Find Events
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>

        {/* Your account and Recent activity */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Your account section - Simplified */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Account</h2>

            {/* Points and Savings */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-28 h-28 rounded-full border-4 border-gray-100 flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
                  <div className="w-24 h-24 rounded-full border-4 border-transparent bg-gradient-to-r from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                    <div className="text-center">
                      <StarIcon className="h-6 w-6 text-white mx-auto mb-1" />
                      <span className="text-2xl font-bold text-white block">{points.toLocaleString('en-IN')}</span>
                      <span className="text-xs text-white/80">points</span>
                    </div>
                  </div>
                </div>
              </div>
              <span className="mx-6 text-2xl font-bold text-gray-300">=</span>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-28 h-28 rounded-full border-4 border-gray-100 flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
                  <div className="w-24 h-24 rounded-full border-4 border-transparent bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                    <div className="text-center">
                      <GiftIcon className="h-6 w-6 text-white mx-auto mb-1" />
                      <span className="text-lg font-bold text-white block">₹{points.toLocaleString('en-IN')}</span>
                      <span className="text-xs text-white/80">to spend</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick tip */}
            <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-xl p-4 border border-primary-100">
              <p className="text-sm text-gray-600 text-center">
                <span className="font-semibold text-primary-600">1 point = ₹1</span> at partner restaurants
              </p>
            </div>
          </div>

          {/* Recent activity section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
              <Link
                to="/transactions"
                className="text-sm text-primary-600 hover:text-primary-700 font-semibold flex items-center group"
              >
                View all
                <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="space-y-3">
              {(() => {
                // Combine rent payments and restaurant transactions
                const rentPayments = (dashboardData?.recentRentPayments || []).map(p => ({
                  ...p,
                  type: 'rent',
                  date: new Date(p.paymentDate)
                }));
                const transactions = (dashboardData?.recentTransactions || []).map(t => ({
                  ...t,
                  type: 'redemption',
                  date: new Date(t.createdAt)
                }));
                const allActivity = [...rentPayments, ...transactions]
                  .sort((a, b) => b.date - a.date)
                  .slice(0, 5);

                if (allActivity.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                      <ArrowPathIcon className="h-10 w-10 mb-3 opacity-50" />
                      <p className="text-sm mb-4">No recent activity</p>
                      <div className="w-full space-y-2 opacity-50 mb-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-xs">
                          <span className="text-gray-400">Pay rent → earn points</span>
                          <span className="text-primary-400">+600 pts</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-xs">
                          <span className="text-gray-400">Redeem at restaurants</span>
                          <span className="text-green-400">Save ₹200</span>
                        </div>
                      </div>
                      <Link to="/pay-rent" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                        Make your first payment →
                      </Link>
                    </div>
                  );
                }

                return allActivity.map((item, index) => {
                  if (item.type === 'rent') {
                    return (
                      <div
                        key={item._id}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-all hover:border-primary-200"
                      >
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center mr-4">
                            <CurrencyDollarIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Rent Payment</p>
                            <p className="text-sm text-gray-500">
                              {item.date.toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 text-lg">₹{item.amount?.toLocaleString('en-IN')}</p>
                          <p className="text-sm font-semibold text-green-600 flex items-center justify-end">
                            <StarIcon className="h-4 w-4 mr-1" />
                            +{Math.round(item.rewardEarned || 0)} pts
                          </p>
                        </div>
                      </div>
                    );
                  } else {
                    // Restaurant redemption
                    const statusColors = {
                      confirmed: 'bg-green-100 text-green-700',
                      verified: 'bg-blue-100 text-blue-700',
                      pending_merchant: 'bg-yellow-100 text-yellow-700',
                      pending_verification: 'bg-yellow-100 text-yellow-700',
                      disputed: 'bg-red-100 text-red-700'
                    };
                    const statusLabels = {
                      confirmed: 'Confirmed',
                      verified: 'Verified',
                      pending_merchant: 'Pending',
                      pending_verification: 'Pending',
                      disputed: 'Disputed'
                    };
                    return (
                      <Link
                        key={item._id}
                        to="/transactions"
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-all hover:border-primary-200"
                      >
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-500 flex items-center justify-center mr-4">
                            <BuildingStorefrontIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{item.restaurant?.name || 'Restaurant'}</p>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-gray-500">
                                {item.date.toLocaleDateString('en-IN', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[item.status] || 'bg-gray-100 text-gray-600'}`}>
                                {statusLabels[item.status] || item.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 text-lg">₹{item.originalBillAmount?.toLocaleString('en-IN')}</p>
                          <p className="text-sm font-semibold text-primary-600 flex items-center justify-end">
                            <ReceiptPercentIcon className="h-4 w-4 mr-1" />
                            -{item.pointsDeducted} pts
                          </p>
                        </div>
                      </Link>
                    );
                  }
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardBILT;
